/**
 * 基准轴管理器。
 * 负责创建、管理所有正式基准轴对象。
 */

import * as THREE from 'three';
import { DatumAxis, type DatumAxisOptions } from '../reference/DatumAxis';
import { ReferenceCreationContext } from '../reference/ReferenceCreationContext';
import {
  DATUM_AXIS_HISTORY_ENTITY_ID,
  type DatumAxisHistoryRecord,
  type DatumAxisHistorySnapshot,
  type DatumAxisHistoryVector3,
  type UserCreatedDatumAxisTimelineEntry,
} from '../reference/DatumAxisHistoryTypes';
import type { Viewer } from '../viewer/Viewer';
import { addObject3DToParent } from '../utils/scene-object/addObject3DToParent';

//------ 基准轴JSON序列化 开始------
/**
 * 基准轴 JSON 协议版本。
 */
export enum DatumAxisJSONSchemaVersion {
  /** 初始版本：保存 DatumAxisManager 可恢复的正式基准轴 plain data。 */
  V1 = 1,
}

/**
 * 基准轴管理器 JSON 文档。
 *
 * 设计说明：
 * 1. 该结构复用历史快照 plain data，避免 Manager 内维护第二套并行持久化模型；
 * 2. `schemaVersion` 只描述 Manager 级 JSON 协议，不代表完整项目文件协议；
 * 3. 当前不接入离线包导入导出，只供上层后续统一编排。
 */
export interface DatumAxisDocumentJSON extends DatumAxisHistorySnapshot {
  /** JSON 协议版本。 */
  readonly schemaVersion: DatumAxisJSONSchemaVersion.V1;
}
//------ 基准轴JSON序列化 结束------


/**
 * 基准轴管理器配置。
 */
export interface DatumAxisManagerOptions {
  /** 默认颜色。 */
  defaultColor?: number;
}

/**
 * 基准轴管理器。
 */
export class DatumAxisManager {
  /** 基准轴根组名称。 */
  private static readonly ROOT_GROUP_NAME = 'DatumAxisGroup';
  /** 基准轴标签根组名称。 */
  private static readonly LABEL_GROUP_NAME = 'DatumAxisLabelGroup';
  /** Viewer 实例。 */
  private readonly viewer: Viewer;
  /** 场景引用。 */
  private readonly scene: THREE.Scene;
  /** 基准轴根组。 */
  private readonly rootGroup: THREE.Group;
  /** 基准轴标签根组。 */
  private readonly labelGroup: THREE.Group;
  /** 基准轴集合。 */
  private readonly axes: Map<number, DatumAxis> = new Map();
  /** 默认颜色。 */
  private readonly defaultColor: number;
  /** 用户创建基准轴名称前缀。 */
  private static readonly CUSTOM_AXIS_NAME_PREFIX = '基准轴';
  /**
   * 用户手动创建基准轴的设计树时间线登记数组。
   *
   * 设计说明：
   * 1. 与 `axes` Map 解耦，专门服务设计树按创建时间排序；
   * 2. 当前没有默认内置轴，用户创建轴都会写入该数组；
   * 3. Undo/Redo 恢复时通过历史记录中的 `createdAt` 重建该数组。
   */
  private readonly userCreatedAxisTimeline: UserCreatedDatumAxisTimelineEntry[] = [];

  /**
   * @param viewer Viewer 实例。
   * @param options 配置参数。
   */
  public constructor(viewer: Viewer, options: DatumAxisManagerOptions = {}) {
    this.viewer = viewer;
    this.scene = viewer.scene;
    this.rootGroup = new THREE.Group();
    this.rootGroup.name = DatumAxisManager.ROOT_GROUP_NAME;
    this.labelGroup = new THREE.Group();
    this.labelGroup.name = DatumAxisManager.LABEL_GROUP_NAME;
    addObject3DToParent(this.rootGroup, this.scene);
    addObject3DToParent(this.labelGroup, this.scene);
    this.defaultColor = options.defaultColor ?? 0x4a9eff;
  }

  /**
   * 创建基准轴。
   *
   * @param options 构造参数。
   * @returns 基准轴对象。
   */
  public createAxis(options: DatumAxisOptions): DatumAxis {
    const axis = new DatumAxis({
      ...options,
      name: options.name.trim().length > 0 ? options.name : this.generateNextCustomAxisName(),
      color: options.color ?? this.defaultColor,
    });
    this.axes.set(axis.datumAxisId, axis);
    addObject3DToParent(axis, this.rootGroup);
    addObject3DToParent(axis.getLabelObject(), this.labelGroup);
    if (axis.creationContext !== ReferenceCreationContext.DefaultBuiltIn) {
      this.registerUserCreatedAxisTimelineEntry(axis.datumAxisId, options.createdAt);
    }
    return axis;
  }

  /**
   * 删除基准轴。
   *
   * @param axisId 基准轴 ID。
   */
  public removeAxis(axisId: number): void {
    const axis = this.axes.get(axisId);
    if (!axis) {
      return;
    }
    this.rootGroup.remove(axis);
    this.labelGroup.remove(axis.getLabelObject());
    axis.dispose();
    this.axes.delete(axisId);
    this.unregisterUserCreatedAxisTimelineEntry(axisId);
  }

  /**
   * 读取用户手动创建基准轴的创建时间戳。
   *
   * @param axisId 基准轴稳定业务 ID。
   * @returns 创建时间戳（毫秒）；非用户创建基准轴时返回 `undefined`。
   */
  public getUserCreatedAxisCreatedAt(axisId: number): number | undefined {
    return this.userCreatedAxisTimeline.find((entry) => entry.axisId === axisId)?.createdAt;
  }

  /**
   * 登记用户手动创建基准轴的设计树时间线项。
   *
   * @param axisId 基准轴稳定业务 ID。
   * @param createdAt 创建时间戳；缺省时写入当前时间。
   */
  private registerUserCreatedAxisTimelineEntry(axisId: number, createdAt?: number): void {
    const existingEntry = this.userCreatedAxisTimeline.find((entry) => entry.axisId === axisId);
    if (existingEntry) {
      return;
    }
    this.userCreatedAxisTimeline.push({
      axisId,
      createdAt: Number.isFinite(createdAt) ? Number(createdAt) : Date.now(),
    });
  }

  /**
   * 移除用户手动创建基准轴的设计树时间线项。
   *
   * @param axisId 基准轴稳定业务 ID。
   */
  private unregisterUserCreatedAxisTimelineEntry(axisId: number): void {
    const entryIndex = this.userCreatedAxisTimeline.findIndex((entry) => entry.axisId === axisId);
    if (entryIndex >= 0) {
      this.userCreatedAxisTimeline.splice(entryIndex, 1);
    }
  }

  /**
   * 获取基准轴。
   *
   * @param axisId 基准轴 ID。
   * @returns 基准轴对象；未命中时返回 `undefined`。
   */
  public getAxis(axisId: number): DatumAxis | undefined {
    return this.axes.get(axisId);
  }

  /**
   * 重命名基准轴。
   *
   * @param axisId 基准轴 ID。
   * @param nextName 新名称。
   * @returns 是否重命名成功。
   */
  public renameAxis(axisId: number, nextName: string): boolean {
    const axis = this.axes.get(axisId);
    const normalizedName = nextName.trim();
    if (!axis || normalizedName.length === 0) {
      return false;
    }
    axis.setName(normalizedName);
    this.viewer.requestRender();
    return true;
  }

  /**
   * 获取全部基准轴。
   *
   * @returns 基准轴集合。
   */
  public getAllAxes(): DatumAxis[] {
    return Array.from(this.axes.values());
  }

  /**
   * 设置基准轴可见性。
   *
   * @param axisId 基准轴 ID。
   * @param visible 是否可见。
   */
  public setAxisVisible(axisId: number, visible: boolean): void {
    this.axes.get(axisId)?.setVisible(visible);
  }

  /**
   * 隐藏全部基准轴。
   */
  public hideAllAxes(): void {
    this.axes.forEach((axis) => {
      axis.setVisible(false);
    });
  }

  //------ 基准轴JSON序列化 开始------
  /**
   * 导出当前基准轴管理器 JSON。
   *
   * 设计说明：
   * 1. 只导出用户创建的正式基准轴，未来若存在默认内置轴，仍由 `createHistorySnapshot()` 统一过滤；
   * 2. 返回值只包含 plain data，不包含 Three.js 对象、材质、标签或场景引用；
   * 3. 当前 API 不写入 `.puppy`，只提供 Manager 级序列化能力。
   *
   * @returns 可持久化的基准轴 JSON 文档。
   */
  public toJSON(): DatumAxisDocumentJSON {
    const snapshot = this.createHistorySnapshot();

    /**
     * 通过 JSON 克隆主动移除 `undefined` 与运行时原型，保证调用方拿到的就是可写盘数据。
     */
    return this.cloneDatumAxisDocumentJSON({
      schemaVersion: DatumAxisJSONSchemaVersion.V1,
      ...snapshot,
    });
  }

  /**
   * 从 JSON 恢复基准轴管理器状态。
   *
   * 设计说明：
   * 1. 先校验协议版本与必要字段，避免无效 JSON 进入对象重建流程；
   * 2. 复用 `restoreHistorySnapshot()` 完成删除旧用户基准轴、重建对象、清理选择和刷新渲染；
   * 3. 当前只恢复 Manager 掌握的正式轴对象，不恢复 `ReferenceAxisController` 中的可复算特征记录。
   *
   * @param data 外部传入的基准轴 JSON。
   */
  public fromJSON(data: unknown): void {
    this.assertDatumAxisDocumentJSON(data);
    const document = this.cloneDatumAxisDocumentJSON(data);

    /**
     * Manager 的恢复入口只消费历史快照字段，`schemaVersion` 在校验后不再传入。
     */
    this.restoreHistorySnapshot({
      managerId: document.managerId,
      axes: document.axes,
      features: document.features,
    });
  }

  /**
   * 克隆基准轴 JSON 文档。
   *
   * @param document 来源 JSON 文档。
   * @returns 去除运行时引用后的 JSON 文档。
   */
  private cloneDatumAxisDocumentJSON(document: DatumAxisDocumentJSON): DatumAxisDocumentJSON {
    return JSON.parse(JSON.stringify(document)) as DatumAxisDocumentJSON;
  }

  /**
   * 断言未知值是基准轴 JSON 文档。
   *
   * @param data 外部输入。
   */
  private assertDatumAxisDocumentJSON(data: unknown): asserts data is DatumAxisDocumentJSON {
    if (!this.isRecord(data)) {
      throw new Error('基准轴 JSON 必须是对象。');
    }
    if (data.schemaVersion !== DatumAxisJSONSchemaVersion.V1) {
      throw new Error(`不支持的基准轴 JSON 版本：${String(data.schemaVersion)}`);
    }
    if (data.managerId !== DATUM_AXIS_HISTORY_ENTITY_ID) {
      throw new Error(`基准轴 JSON.managerId 不匹配：${String(data.managerId)}`);
    }
    if (!Array.isArray(data.axes)) {
      throw new Error('基准轴 JSON.axes 必须是数组。');
    }
    if (!Array.isArray(data.features)) {
      throw new Error('基准轴 JSON.features 必须是数组。');
    }

    /**
     * 正式轴对象恢复依赖轴线几何和显示端点，因此这里逐条校验，避免恢复中途才出现难定位错误。
     */
    const axisIds = new Set<number>();
    data.axes.forEach((record, index) => {
      this.assertDatumAxisHistoryRecord(record, index);
      if (axisIds.has(record.axisId)) {
        throw new Error(`基准轴 JSON.axes[${index}].axisId 重复：${record.axisId}`);
      }
      axisIds.add(record.axisId);
    });
  }

  /**
   * 断言未知值是单条基准轴历史记录。
   *
   * @param value 外部输入。
   * @param index 数组下标。
   */
  private assertDatumAxisHistoryRecord(
    value: unknown,
    index: number,
  ): asserts value is DatumAxisHistoryRecord {
    if (!this.isRecord(value)) {
      throw new Error(`基准轴 JSON.axes[${index}] 必须是对象。`);
    }
    if (!Number.isFinite(value.axisId)) {
      throw new Error(`基准轴 JSON.axes[${index}].axisId 必须是有限数字。`);
    }
    if (typeof value.name !== 'string' || value.name.trim().length === 0) {
      throw new Error(`基准轴 JSON.axes[${index}].name 必须是非空字符串。`);
    }
    if (!this.isReferenceCreationContext(value.creationContext)) {
      throw new Error(`基准轴 JSON.axes[${index}].creationContext 不合法。`);
    }
    if (value.creationContext === ReferenceCreationContext.DefaultBuiltIn) {
      throw new Error(`基准轴 JSON.axes[${index}].creationContext 不允许恢复默认内置对象。`);
    }

    this.assertDatumAxisHistoryVector3(value.originWorld, `基准轴 JSON.axes[${index}].originWorld`);
    this.assertDatumAxisHistoryVector3(value.direction, `基准轴 JSON.axes[${index}].direction`);
    this.assertDatumAxisHistoryVector3(value.startWorld, `基准轴 JSON.axes[${index}].startWorld`);
    this.assertDatumAxisHistoryVector3(value.endWorld, `基准轴 JSON.axes[${index}].endWorld`);

    if (!Number.isFinite(value.color)) {
      throw new Error(`基准轴 JSON.axes[${index}].color 必须是有限数字。`);
    }
    if (typeof value.visible !== 'boolean') {
      throw new Error(`基准轴 JSON.axes[${index}].visible 必须是布尔值。`);
    }
    if (!Number.isFinite(value.opacity)) {
      throw new Error(`基准轴 JSON.axes[${index}].opacity 必须是有限数字。`);
    }
    if (
      value.createdAt !== undefined
      && !Number.isFinite(value.createdAt)
    ) {
      throw new Error(`基准轴 JSON.axes[${index}].createdAt 必须是有限数字。`);
    }
  }

  /**
   * 断言未知值是基准轴历史三维向量。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertDatumAxisHistoryVector3(
    value: unknown,
    fieldName: string,
  ): asserts value is DatumAxisHistoryVector3 {
    if (
      !Array.isArray(value)
      || value.length !== 3
      || value.some((item) => !Number.isFinite(item))
    ) {
      throw new Error(`${fieldName} 必须是三个有限数字。`);
    }
  }

  /**
   * 判断未知值是否为普通记录对象。
   *
   * @param value 待判断值。
   * @returns 是否为普通记录对象。
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
  }

  /**
   * 判断未知值是否为合法创建来源。
   *
   * @param value 待判断值。
   * @returns 是否为合法创建来源。
   */
  private isReferenceCreationContext(value: unknown): value is ReferenceCreationContext {
    return Object.values(ReferenceCreationContext).includes(value as ReferenceCreationContext);
  }
  //------ 基准轴JSON序列化 结束------

  //------ 基准轴撤销重做 开始------
  /**
   * 捕获用户创建基准轴的历史快照。
   *
   * 设计说明：
   * 1. 基准轴没有默认内置对象，但仍按创建上下文过滤，避免未来内置轴污染模型历史；
   * 2. 用户创建的零件态和装配体态基准轴需要保存稳定业务 ID，保证 Redo 后引用 ID 不变化；
   * 3. 快照只保存 plain data，不保存 THREE.Object3D、材质或标签对象。
   *
   * @returns 基准轴历史快照。
   */
  public createHistorySnapshot(): DatumAxisHistorySnapshot {
    return {
      managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
      axes: this.getAllAxes()
        .filter((axis) => axis.creationContext !== ReferenceCreationContext.DefaultBuiltIn)
        .map((axis) => this.createHistoryRecordFromAxis(axis)),
      features: [],
    };
  }

  /**
   * 按历史快照恢复用户创建的基准轴。
   *
   * @param snapshot 基准轴历史快照。
   */
  public restoreHistorySnapshot(snapshot: DatumAxisHistorySnapshot): void {
    this.getAllAxes()
      .filter((axis) => axis.creationContext !== ReferenceCreationContext.DefaultBuiltIn)
      .forEach((axis) => {
        this.removeAxis(axis.datumAxisId);
      });
    this.userCreatedAxisTimeline.length = 0;

    (snapshot.axes ?? []).forEach((record, index) => {
      this.createAxis({
        id: record.axisId,
        creationContext: record.creationContext,
        name: record.name,
        originWorld: this.createVectorFromHistory(record.originWorld),
        direction: this.createVectorFromHistory(record.direction),
        startWorld: this.createVectorFromHistory(record.startWorld),
        endWorld: this.createVectorFromHistory(record.endWorld),
        color: record.color,
        visible: record.visible,
        opacity: record.opacity,
        createdAt: Number.isFinite(record.createdAt)
          ? Number(record.createdAt)
          : Date.now() + index,
      });
    });

    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.requestRender();
  }

  /**
   * 从基准轴创建历史记录。
   *
   * @param axis 基准轴对象。
   * @returns 基准轴历史记录。
   */
  private createHistoryRecordFromAxis(axis: DatumAxis): DatumAxisHistoryRecord {
    const endpoints = axis.getWorldEndpoints();
    const displaySnapshot = axis.getDisplaySnapshot();

    return {
      axisId: axis.datumAxisId,
      name: axis.name,
      creationContext: axis.creationContext,
      originWorld: this.createHistoryVector(axis.originWorld),
      direction: this.createHistoryVector(axis.direction),
      startWorld: this.createHistoryVector(endpoints?.start ?? axis.originWorld),
      endWorld: this.createHistoryVector(endpoints?.end ?? axis.originWorld),
      color: displaySnapshot.color,
      visible: axis.isVisible(),
      opacity: displaySnapshot.opacity,
      createdAt: this.getUserCreatedAxisCreatedAt(axis.datumAxisId),
    };
  }

  /**
   * 创建历史向量。
   *
   * @param vector Three.js 向量。
   * @returns plain data 向量。
   */
  private createHistoryVector(vector: THREE.Vector3): DatumAxisHistoryVector3 {
    return [vector.x, vector.y, vector.z];
  }

  /**
   * 从历史向量创建 Three.js 向量。
   *
   * @param vector 历史向量。
   * @returns Three.js 向量。
   */
  private createVectorFromHistory(vector: DatumAxisHistoryVector3): THREE.Vector3 {
    return new THREE.Vector3(vector[0], vector[1], vector[2]);
  }
  //------ 基准轴撤销重做 结束------

  /**
   * 释放全部资源。
   */
  public dispose(): void {
    this.getAllAxes().forEach((axis) => {
      this.rootGroup.remove(axis);
      this.labelGroup.remove(axis.getLabelObject());
      axis.dispose();
    });
    this.axes.clear();
    this.userCreatedAxisTimeline.length = 0;
    this.scene.remove(this.rootGroup);
    this.scene.remove(this.labelGroup);
  }

  /**
   * 生成下一个用户创建基准轴名称。
   *
   * @returns 形如“基准轴1”的名称。
   */
  private generateNextCustomAxisName(): string {
    const prefix = DatumAxisManager.CUSTOM_AXIS_NAME_PREFIX;
    let nextIndex = 1;
    const usedNames = new Set(this.getAllAxes().map((axis) => axis.name));
    while (usedNames.has(`${prefix}${nextIndex}`)) {
      nextIndex += 1;
    }
    return `${prefix}${nextIndex}`;
  }
}
